import React, { useEffect, useState } from 'react';

const tableStyle = {
  borderCollapse: 'collapse',
  width: '100%'
};

const thTdStyle = {
  border: '1px solid #ccc',
  padding: '3px',
  textAlign: 'center',
  lineHeight: '1.0',
  fontFamily: 'Courier New, monospace',
  fontSize: '14px'
};

const baseRowStyle = {
  lineHeight: '1.0'
};

const baseButtonStyle = {
  borderRadius: '6px',
  padding: '2px 8px',
  border: '1px solid #999',
  backgroundColor: '#f8f8f8',
  cursor: 'pointer',
  marginRight: '6px',
  fontFamily: 'Courier New, monospace',
  fontSize: '14px'
};

const scanButtonStyle = {
  ...baseButtonStyle,
  backgroundColor: 'lightgreen',
  marginRight: 0
};

// Red for "bad" scans
const badScanButtonStyle = {
  ...scanButtonStyle,
  backgroundColor: 'red'
};

// If the stub has been downloaded, we color the stub button differently
const downloadedStubButtonStyle = {
  ...baseButtonStyle,
  backgroundColor: '#b3d9ff'
};

// If the sample has been downloaded, color the sample button differently
const downloadedSampleButtonStyle = {
  ...baseButtonStyle,
  backgroundColor: '#b3d9ff',
  marginRight: 0
};

const storeBadgeStyle = {
  display: 'inline-block',
  backgroundColor: '#e8d5f5',
  border: '1px solid #c9a6e3',
  borderRadius: '4px',
  padding: '0px 5px',
  fontSize: '12px',
  fontFamily: 'Courier New, monospace',
  marginLeft: '6px',
  color: '#6b21a8'
};

function timeAgoSince(utcString) {
  if (!utcString) return null;
  const dateObj = new Date(utcString + ' UTC');
  const now = new Date();
  const diffMs = now - dateObj;
  if (isNaN(diffMs)) return null;

  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) {
    return diffMins + ' minute' + (diffMins > 1 ? 's' : '') + ' ago';
  }
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) {
    return diffHrs + ' hour' + (diffHrs > 1 ? 's' : '') + ' ago';
  }
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 30) {
    return diffDays + ' day' + (diffDays > 1 ? 's' : '') + ' ago';
  }
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) {
    return diffMonths + ' month' + (diffMonths > 1 ? 's' : '') + ' ago';
  }
  const diffYears = Math.floor(diffMonths / 12);
  return diffYears + ' year' + (diffYears > 1 ? 's' : '') + ' ago';
}

function App() {
  const [trials, setTrials] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [perPage, setPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [nameFilter, setNameFilter] = useState('');

  // Check if we should show all (localStorage "show_all" = "1")
  const showAll = window.localStorage.getItem('show_all') === '1';

  useEffect(() => {
    fetchData(page, nameFilter);
  }, [page, nameFilter]);

  async function fetchData(pageNumber, filter) {
    setLoading(true);
    try {
      let url = `./get_approved_trials.php?page=${pageNumber}`;
      if (showAll) {
        url += `&show_all=1`;
      }
      if (filter) {
        url += `&name_filter=${encodeURIComponent(filter)}`;
      }
      const response = await fetch(url);
      const json = await response.json();
      setTrials(json.data);
      setTotalCount(json.totalCount);
      setPerPage(json.perPage);
    } catch (e) {
      console.error('Fetch error:', e);
    }
    setLoading(false);
  }

  function handleNameFilterChange(e) {
    setNameFilter(e.target.value);
    setPage(1);
  }

  function handlePrevPage() {
    if (page > 1) {
      setPage(page - 1);
    }
  }

  function handleNextPage() {
    const totalPages = Math.ceil(totalCount / perPage);
    if (page < totalPages) {
      setPage(page + 1);
    }
  }

  // Build the URL to the download.php script
  function getDownloadUrl(trialId, type) {
    return `./download.php?id=${trialId}&type=${type}`;
  }

  // Immediately update local state so user sees real-time color change
  function handleDownloadClick(trialId, type) {
    window.open(getDownloadUrl(trialId, type), '_blank');

    const nowUTC = new Date().toISOString().slice(0, 19).replace('T', ' ');

    setTrials(prevTrials =>
      prevTrials.map(t => {
        if (t.id === trialId) {
          if (type === 'stub') {
            return { ...t, stubDownloadedTime: nowUTC };
          } else {
            return { ...t, sampleDownloadedTime: nowUTC };
          }
        }
        return t;
      })
    );
  }

  return (
    <div style={{ margin: '20px', fontFamily: 'Courier New, monospace' }}>
      {loading && <p>Loading...</p>}

      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thTdStyle}>Time</th>
            <th style={thTdStyle}>
              Name
              <br />
              <input
                type="text"
                value={nameFilter}
                onChange={handleNameFilterChange}
                placeholder="Filter name/store..."
                style={{
                  fontFamily: 'Courier New, monospace',
                  fontSize: '12px',
                  padding: '2px 4px',
                  marginTop: '2px',
                  width: '80%'
                }}
              />
            </th>
            <th style={thTdStyle}>Scan</th>
            {showAll && (
              <th style={thTdStyle}>Target DLL</th>
            )}
            <th style={thTdStyle}>Download</th>
          </tr>
        </thead>
        <tbody>
          {trials.map(trial => {
            const scanDisabled = !trial.scan_id;

            const rowHoverStyle = {
              ...baseRowStyle,
              backgroundColor: trial.id === hoveredRowId ? '#FFFF00' : 'transparent'
            };

            const {
              stubDownloadedTime,
              sampleDownloadedTime,
              target_dll,
              store
            } = trial;

            const stubTooltip = stubDownloadedTime
              ? `Downloaded ${timeAgoSince(stubDownloadedTime)}`
              : '';
            const sampleTooltip = sampleDownloadedTime
              ? `Downloaded ${timeAgoSince(sampleDownloadedTime)}`
              : '';

            const stubButtonStyle = stubDownloadedTime
              ? downloadedStubButtonStyle
              : baseButtonStyle;
            const sampleButtonStyle = sampleDownloadedTime
              ? downloadedSampleButtonStyle
              : { ...baseButtonStyle, marginRight: 0 };

            // Decide if it's a "bad" (approved=0) scan => red button
            const isBadScan = (trial.approved === 0 && trial.scan_id);
            const finalScanButtonStyle = isBadScan ? badScanButtonStyle : scanButtonStyle;

            return (
              <tr
                key={trial.id}
                style={rowHoverStyle}
                onMouseEnter={() => setHoveredRowId(trial.id)}
                onMouseLeave={() => setHoveredRowId(null)}
              >
                <td style={thTdStyle}>{trial.relative_time}</td>

                <td style={{ ...thTdStyle, position: 'relative' }}>
                  {trial.tag && (
                    <span style={{ float: 'left', marginRight: '6px' }}>
                      {trial.tag}
                    </span>
                  )}
                  {trial.name}
                  {store && (
                    <span style={storeBadgeStyle}>
                      {store}
                    </span>
                  )}
                  {trial.time != -1 && (
                    <span style={{ float: 'right', marginRight: '2px' }}>
                      (t={trial.time}s)
                    </span>
                  )}
                  <span style={{ float: 'right' }}>({trial.fileSize})</span>
                </td>

                <td style={thTdStyle}>
                  {scanDisabled ? (
                    <button style={{ ...finalScanButtonStyle, opacity: 0.5 }} disabled>
                      No Scan
                    </button>
                  ) : (
                    <button
                      style={finalScanButtonStyle}
                      onClick={() => window.open(`https://kleenscan.com/scan_result/${trial.scan_id}`, '_blank')}
                    >
                      View Scan
                    </button>
                  )}
                </td>

                {showAll && (
                  <td style={thTdStyle}>
                    {target_dll || ''}
                  </td>
                )}

                <td style={thTdStyle}>
                  <button
                    style={stubButtonStyle}
                    title={stubTooltip}
                    onClick={() => handleDownloadClick(trial.id, 'stub')}
                  >
                    Stub
                  </button>
                  <button
                    style={sampleButtonStyle}
                    title={sampleTooltip}
                    onClick={() => handleDownloadClick(trial.id, 'sample')}
                  >
                    Sample
                  </button>
                </td>
              </tr>
            );
          })}

          {!loading && trials.length === 0 && (
            <tr>
              <td colSpan={showAll ? 5 : 4} style={thTdStyle}>
                No approved Trials found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div
        style={{
          marginTop: '10px',
          display: 'flex',
          justifyContent: 'center',
          gap: '20px'
        }}
      >
        <button
          style={baseButtonStyle}
          onClick={handlePrevPage}
          disabled={page <= 1}
        >
          Prev
        </button>
        <span style={{ alignSelf: 'center' }}>
          Page {page} of {Math.ceil(totalCount / perPage)}
        </span>
        <button
          style={baseButtonStyle}
          onClick={handleNextPage}
          disabled={page >= Math.ceil(totalCount / perPage)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default App;